import { useState } from 'react';
import { Button, Input, Modal, NativeSelect } from '@immo/ui-kit';
import { FleetCommandBodySchema, type FleetCommandView } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { CommandOutput, FollowedCommand, FollowedCommandFooter } from './followed-command';
import { formatCommandLine, splitCommandLine } from './command-line';

/** What the command input starts with: every command a host runs is a pnpm one (fleet-agent ADR 0016). */
export const RUN_COMMAND_PREFILL = 'pnpm ';

type Props = {
  readonly machineName: string;
  /** The projects the machine's workers serve — the only ones a command may name. */
  readonly projects: ReadonlyArray<string>;
  /** The issued command, as the page's poll last read it; `null` while the form is up. */
  readonly command: FleetCommandView | null;
  readonly busy: boolean;
  /** Why the backend refused the issue, translated. */
  readonly error: string | null;
  readonly onSubmit: (project: string, argv: ReadonlyArray<string>) => void;
  /** Back to the form, with the last project and line still in it. */
  readonly onAgain: () => void;
  readonly onClose: () => void;
};

/**
 * `run-command` (fleet-agent ADR 0016): one pnpm command in one project's
 * checkout on this host. Two phases in one dialog — the form, then the issued
 * command followed until it settles with its exit code and the tail of its
 * output (`FollowedCommand`).
 *
 * The line is split like the CLI splits it; whether its words may run is
 * `FleetCommandBodySchema`'s call, the check the backend and the host make
 * again, and its refusal shows under the input.
 */
export function RunCommandDialog({ machineName, projects, command, busy, error, onSubmit, onAgain, onClose }: Props) {
  const t = useTranslations('pages.admin.fleet.run-command');
  const tFleet = useTranslations('pages.admin.fleet');
  const [project, setProject] = useState(projects.length === 1 ? (projects[0] as string) : '');
  const [line, setLine] = useState(RUN_COMMAND_PREFILL);

  if (command !== null) {
    const payload = command.payload?.kind === 'run-command' ? command.payload : null;

    return (
      <Modal
        open
        onClose={onClose}
        confirmOnClose={false}
        size="lg"
        title={t('title')}
        footer={
          <FollowedCommandFooter command={command} againLabel={t('again-cta')} onAgain={onAgain} onClose={onClose} />
        }
      >
        <FollowedCommand
          scope={t('scope-line', { machine: machineName, project: command.project ?? '' })}
          subject={formatCommandLine(command.argv ?? [])}
          command={command}
        >
          {payload !== null && (
            <>
              <p className="font-mono text-base-sm text-text-primary">
                {payload.exitCode === null ? t('no-exit-code') : t('exit-code', { code: String(payload.exitCode) })}
              </p>
              <CommandOutput output={payload.output} truncated={payload.truncated} />
            </>
          )}
        </FollowedCommand>
      </Modal>
    );
  }

  const validation = validate(project, line);
  const touched = line !== RUN_COMMAND_PREFILL;
  const lineError =
    validation.unclosedQuote !== undefined
      ? t('unclosed-quote', { quote: validation.unclosedQuote })
      : validation.argvError;
  const submit = () => {
    if (validation.argv !== null && project !== '') onSubmit(project, validation.argv);
  };

  return (
    <Modal
      open
      onClose={() => (busy ? undefined : onClose())}
      confirmOnClose={false}
      size="md"
      title={t('title')}
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose} disabled={busy}>
            {tFleet('cancel-cta')}
          </Button>
          <Button type="button" onClick={submit} disabled={busy || validation.argv === null || project === ''}>
            {t('cta')}
          </Button>
        </>
      }
    >
      <p className="font-mono text-base-sm text-text-secondary">{machineName}</p>
      <p className="mt-3 text-sm text-text-secondary">{t('body')}</p>

      <form
        className="mt-4 flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        {projects.length === 0 ? (
          <p className="text-sm text-warning-700">{t('no-projects')}</p>
        ) : (
          <NativeSelect
            value={project}
            onChange={setProject}
            disabled={busy}
            aria-label={t('project-field')}
            placeholder={t('project-placeholder')}
            options={projects.map((name) => ({ value: name, label: name }))}
          />
        )}
        <Input
          className="font-mono text-base-sm"
          autoFocus
          value={line}
          onChange={(event) => setLine(event.target.value)}
          disabled={busy}
          invalid={touched && lineError !== null}
          aria-label={t('command-field')}
          spellCheck={false}
          autoComplete="off"
        />
        {touched && lineError !== null ? (
          <p role="alert" className="text-sm text-danger-600">
            {lineError}
          </p>
        ) : (
          <p className="text-sm text-text-tertiary">{t('command-hint')}</p>
        )}
      </form>

      {error !== null && (
        <p role="alert" className="mt-3 text-sm text-danger-600">
          {error}
        </p>
      )}
    </Modal>
  );
}

type Validation = {
  /** The words to send, or `null` while the line may not run. */
  readonly argv: ReadonlyArray<string> | null;
  readonly unclosedQuote?: '"' | "'";
  /** The contract's own refusal of the words, as it words it. */
  readonly argvError: string | null;
};

function validate(project: string, line: string): Validation {
  const split = splitCommandLine(line);

  if (!split.ok) return { argv: null, unclosedQuote: split.unclosedQuote, argvError: null };

  const parsed = FleetCommandBodySchema.safeParse({ verb: 'run-command', project, argv: split.argv });

  if (parsed.success) return { argv: split.argv, argvError: null };

  // A missing project only keeps the button disabled; the select says what is missing.
  const argvIssue = parsed.error.issues.find((issue) => issue.path[0] === 'argv');

  return { argv: argvIssue === undefined ? split.argv : null, argvError: argvIssue?.message ?? null };
}
